import { ReactNode } from 'react'

import { Head, usePage } from '@inertiajs/react'
import { Toaster } from '@/components/ui/sonner'
import { Logo } from '@/components/ui/logo'
import { PageProps } from '@/types'
import { PaperClip, CoffeeRing, SquiggleDoodle } from '@/components/sketch/Doodles'

interface AuthLayoutProps {
  children: ReactNode
  title?: string
  heading: string
  description?: string
}

export function AuthLayout({ children, title, heading, description }: AuthLayoutProps) {
  const { flash } = usePage<PageProps>().props

  return (
    <>
      <Head title={title || 'Corppy'} />

      <div className="relative flex min-h-svh flex-col font-display">
        {/* Doodles — texture in the margins, hidden on phones */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden sm:block">
          <PaperClip className="absolute left-8 top-8 h-11 w-6 -rotate-6 opacity-[0.14]" />
          <CoffeeRing className="absolute right-10 top-24 h-12 w-12 opacity-[0.12]" />
          <SquiggleDoodle className="absolute bottom-10 left-10 h-5 w-12 opacity-[0.15]" />
        </div>

        {/* Header */}
        <header className="relative z-10 flex items-center px-5 py-4 border-b border-black/[0.04]">
          <Logo size="md" />
        </header>

        {/* Form content */}
        <div className="relative z-10 flex flex-1 items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-[380px]">
            {/* Flash messages */}
            {flash.notice && (
              <div className="auth-fade-in mb-6 py-2.5 px-4 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg border border-emerald-200/60">
                {flash.notice}
              </div>
            )}
            {flash.alert && (
              <div className="auth-fade-in mb-6 py-2.5 px-4 bg-red-50 text-red-700 text-sm font-medium rounded-lg border border-red-200/60">
                {flash.alert}
              </div>
            )}

            {/* Heading */}
            <div className="auth-fade-in mb-8" style={{ animationDelay: '60ms' }}>
              <h1 className="text-[1.625rem] font-extrabold tracking-[-0.02em] text-foreground">
                {heading}
              </h1>
              {description && (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {description}
                </p>
              )}
            </div>

            <div className="auth-fade-in" style={{ animationDelay: '120ms' }}>
              {children}
            </div>
          </div>
        </div>
      </div>

      <Toaster />
    </>
  )
}
